import React, { useCallback } from "react";

import RRDDirectBulkSelectionForm from "./RRDDirectBulkSelectionForm";
import BulkSelectionLayout from "@/features/campaigns/components/bulkSelection/BulkSelectionLayout";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";
import {
  rrdDisplayBulkSelectionAppliedMsg,
  rrdDisplayBulkSelectionFailedMsg,
  rrdDisplayFillAllFieldsMsg,
  rrdDisplaySelectLocationMsg,
} from "./RRDBulkSelectionMsg";
import { supportsRRDDifferentExpiry } from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";
import {
  getRRDStoreCopyDefaultQuantity,
  getRRDStoreCopyFormFields,
  resolveRRDStoreCopyConfig,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdStoreCopyConfigHelper";

type RRDRecord = Record<string, any>;

type RRDDirectMailBulkSelectionProps = {
  onToggleBulkSelection?: (_isOpened: boolean) => void;
  storeCopyCpm?: any;
  campaignData?: RRDRecord;
  setLocationFieldValue: (_field: string, _value: any) => void;
  checkedLocations?: any[];
  minCouponDurationAdmin?: any;
  lastCouponExpiryDateAdmin?: any;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | null;
  locations?: RRDRecord[];
  setDisableLocations: (_locationIds: any[]) => void;
  disableLocations?: any[];
  marketList?: RRDRecord[];
};

function RRDDirectMailBulkSelection({
  onToggleBulkSelection,
  storeCopyCpm,
  campaignData,
  setLocationFieldValue,
  checkedLocations,
  minCouponDurationAdmin,
  lastCouponExpiryDateAdmin,
  couponExpirationSetBy,
  fixedCouponDurationDays,
  locations,
  setDisableLocations,
  disableLocations = [],
  marketList,
}: RRDDirectMailBulkSelectionProps) {
  const handleOnSubmitBulkSelection = async (values: RRDRecord) => {
    if (!checkedLocations?.length) {
      rrdDisplaySelectLocationMsg();
      return false;
    }

    const {
      mailing_date,
      different_coupon_expiry,
      front_coupon_expiry_date,
      back_coupon_expiry_date,
      distribution,
      store_copy,
      store_copy_qnt,
      hero_images,
      coupons,
      layout_key,
    } = values;

    const { isValid, notValidMsg } =
      rrdDirectMailCampaignHelper.isBulkSelectionValid(values);

    if (!isValid) {
      rrdDisplayFillAllFieldsMsg(notValidMsg);
      return false;
    }

    const allMismatches: RRDRecord[] = [];

    checkedLocations.forEach((locationId) => {
      const location = locations?.find((loc) => loc?.id === locationId);
      const storeCopyConfig = resolveRRDStoreCopyConfig({
        location,
        campaign: campaignData,
      });
      const storeCopyFields = getRRDStoreCopyFormFields(storeCopyConfig);
      const locationStoreCopyQnt =
        store_copy && getRRDStoreCopyDefaultQuantity(storeCopyConfig)
          ? getRRDStoreCopyDefaultQuantity(storeCopyConfig)
          : store_copy_qnt;

      const {
        belongingHeroImages,
        belongingCoupons,
        notBelongingDetails: mismatchDetails,
      } = rrdDirectMailCampaignHelper.getAssetsBelongsToLocation(location, {
        hero_images,
        coupons,
      });

      if (mismatchDetails?.length) {
        allMismatches.push(...mismatchDetails);
      }

      const { distribution_cost, store_copy_cost, total_cost } =
        rrdDirectMailCampaignHelper.calculateLocationCosts(
          {
            ...values,
            ...storeCopyFields,
            store_copy_qnt: locationStoreCopyQnt,
            hero_images: belongingHeroImages,
            coupons: belongingCoupons,
          },
          storeCopyCpm,
          location?.distribution?.cost
        );

      const backCouponExpiryDate =
        couponExpirationSetBy === "brand" ||
        (supportsRRDDifferentExpiry(layout_key) && !back_coupon_expiry_date)
          ? front_coupon_expiry_date
          : back_coupon_expiry_date;

      setLocationFieldValue(`${locationId}.mailing_date`, mailing_date);
      setLocationFieldValue(
        `${locationId}.different_coupon_expiry`,
        couponExpirationSetBy === "brand" ? false : different_coupon_expiry
      );
      setLocationFieldValue(
        `${locationId}.front_coupon_expiry_date`,
        front_coupon_expiry_date
      );
      setLocationFieldValue(
        `${locationId}.back_coupon_expiry_date`,
        backCouponExpiryDate
      );
      setLocationFieldValue(`${locationId}.distribution`, distribution);
      setLocationFieldValue(
        `${locationId}.distribution_cost`,
        distribution_cost
      );
      setLocationFieldValue(`${locationId}.store_copy`, store_copy);
      setLocationFieldValue(
        `${locationId}.store_copy_qnt`,
        locationStoreCopyQnt
      );
      setLocationFieldValue(`${locationId}.store_copy_cost`, store_copy_cost);
      setLocationFieldValue(
        `${locationId}.store_copy_cpm`,
        storeCopyFields.store_copy_cpm
      );
      setLocationFieldValue(
        `${locationId}.store_copy_min_quantity`,
        storeCopyFields.store_copy_min_quantity
      );
      setLocationFieldValue(
        `${locationId}.store_copy_max_quantity`,
        storeCopyFields.store_copy_max_quantity
      );
      setLocationFieldValue(
        `${locationId}.store_copy_fixed_quantity`,
        storeCopyFields.store_copy_fixed_quantity
      );
      setLocationFieldValue(`${locationId}.total_cost`, total_cost);
      setLocationFieldValue(`${locationId}.hero_images`, belongingHeroImages);
      setLocationFieldValue(`${locationId}.coupons`, belongingCoupons);
    });

    rrdDisplayBulkSelectionAppliedMsg().then(() => {
      if (allMismatches.length > 0) {
        rrdDisplayBulkSelectionFailedMsg(allMismatches);
      }
    });
    return { success: true, allMismatches };
  };

  const handleBulkSelectionChange = useCallback(
    (values: RRDRecord) => {
      const disableLocations =
        rrdDirectMailCampaignHelper.getDisableLocationsWhileBulkSelection(
          values,
          locations || []
        );
      setDisableLocations(disableLocations);
    },
    [locations, setDisableLocations]
  );

  return (
    <div>
      <BulkSelectionLayout
        collapsedHeading="Want to make bulk selections"
        expandedHeading="RRD Direct Mail"
        expandedTitle="Make your selections in bulk"
        onToggleBulkSelection={onToggleBulkSelection}
      >
        <RRDDirectBulkSelectionForm
          onSubmitBulkSelection={handleOnSubmitBulkSelection}
          minCouponDurationAdmin={minCouponDurationAdmin}
          lastCouponExpiryDateAdmin={lastCouponExpiryDateAdmin}
          couponExpirationSetBy={couponExpirationSetBy}
          fixedCouponDurationDays={fixedCouponDurationDays}
          onChangeBulkSelection={handleBulkSelectionChange}
          marketList={marketList}
          locations={locations}
          checkedLocations={checkedLocations}
          disableLocations={disableLocations}
          campaignData={campaignData}
        />
      </BulkSelectionLayout>
    </div>
  );
}

export default RRDDirectMailBulkSelection;
